import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import type { Plan } from "@/types/tenant";
import toast from "react-hot-toast";
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbToggleLeft,
  TbToggleRight,
  TbUsers,
  TbCheckbox,
} from "react-icons/tb";

export const PlatformPlanList: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await TenantService.listPlans();
      setPlans(data);
    } catch (error) {
      console.error("Error loading plans:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleToggleActive = async (plan: Plan) => {
    try {
      await TenantService.updatePlan(plan.id, { is_active: !plan.is_active });
      toast.success(`Plan ${plan.is_active ? "deactivated" : "activated"}`);
      await loadPlans();
    } catch {
      toast.error("Failed to update plan");
    }
  };

  const handleDeletePlan = async (plan: Plan) => {
    if (
      !confirm(
        `Are you sure you want to delete the "${plan.name}" plan? This cannot be undone.`
      )
    )
      return;

    try {
      await TenantService.deletePlan(plan.id);
      toast.success("Plan deleted");
      await loadPlans();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete plan"
      );
    }
  };

  const featureCount = (plan: Plan) => {
    return [
      plan.feature_superset,
      plan.feature_arcgis,
      plan.feature_realtime,
      plan.feature_bulk_assignments,
      plan.feature_reporting,
      plan.feature_asset_management,
    ].filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-[#444]">Plans</h2>
        <button
          onClick={() => navigate("/platform/plans/new")}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] transition-colors cursor-pointer"
        >
          <TbPlus className="w-4 h-4" /> New Plan
        </button>
      </div>

      {/* Plan cards */}
      {plans.length === 0 ? (
        <div className="text-center py-12 text-[#828282] text-sm">
          No plans yet. Create your first plan to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-lg border shadow-sm p-5 space-y-3 ${
                plan.is_active
                  ? "border-gray-200 bg-white"
                  : "border-red-200 bg-red-50"
              }`}
            >
              {/* Plan header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[#444]">
                    {plan.name}
                    {!plan.is_active && (
                      <span className="ml-2 text-xs font-medium text-red-500">
                        (Inactive)
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-[#828282] mt-0.5">
                    {plan.slug}
                    {plan.price_monthly > 0 &&
                      ` · $${plan.price_monthly}/mo`}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigate(`/platform/plans/${plan.id}`)}
                    className="p-2 text-[#828282] hover:text-[#374795] hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                    title="Edit plan"
                  >
                    <TbEdit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(plan)}
                    className="p-2 text-[#828282] hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors cursor-pointer"
                    title={plan.is_active ? "Deactivate" : "Activate"}
                  >
                    {plan.is_active ? (
                      <TbToggleRight className="w-5 h-5 text-green-500" />
                    ) : (
                      <TbToggleLeft className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan)}
                    className="p-2 text-[#828282] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Delete plan"
                  >
                    <TbTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {plan.description && (
                <p className="text-xs text-[#828282]">{plan.description}</p>
              )}

              {/* Limits */}
              <div className="flex items-center gap-4 text-xs text-[#444]">
                <span className="flex items-center gap-1">
                  <TbUsers className="w-3.5 h-3.5 text-[#828282]" />
                  {plan.max_users === 0 ? "Unlimited" : plan.max_users} users
                </span>
                <span className="flex items-center gap-1">
                  <TbCheckbox className="w-3.5 h-3.5 text-[#828282]" />
                  {plan.max_tasks === 0 ? "Unlimited" : plan.max_tasks} tasks
                </span>
                <span className="text-[#828282]">
                  {featureCount(plan)}/6 features
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
